import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { unflatten } from "flat";
import AccessPage from "./AccessPage";
import Footer from "components/Footer";
import Form from "components/Form";
import ContentWrapper from "components/ContentWrapper";
import PageIndicators from "../PageIndicators";
import PermissionsPage from "./PermissionsPage";
import TermsPage from "./TermsPage";
import MemberPage from "./MemberPage";
import { getDisplayedScopes } from "services/identity";
import { getScopes } from "utils";
import { PageWrapper } from "./styles";

type Props = {
  submitForm: (data: any) => void;
  isButtonsDisabled: boolean;
};
const Pages: React.FC<Props> = ({ submitForm, isButtonsDisabled }: Props) => {
  const { t } = useTranslation();
  const BUTTON_TEXT = useMemo(
    // Four-step wizard: Access, Terms, Member, Permissions
    () => [
      t("buttons:next"),
      t("buttons:agree"),
      t("buttons:next"),
      t("buttons:submit"),
    ],
    [t],
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberConfirmRequested, setMemberConfirmRequested] = useState(false);
  const clickNext = useCallback(() => {
    // If we're on the Member step (index 2), trigger its async confirm flow
    if (currentPage === 2) {
      setMemberConfirmRequested(true);
      return;
    }
    if (currentPage < 3) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage]);
  const clickBack = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);
  const scopes = getScopes();
  const displayedScopes = useMemo(() => getDisplayedScopes(scopes), [scopes]);
  const PAGES = [
    <AccessPage key="AccessPage" />,
    <TermsPage key="TermsPage" />,
    <MemberPage
      key="MemberPage"
      onMemberConfirmed={() => {
        setMemberConfirmRequested(false);
        setCurrentPage((prev: number) => Math.min(prev + 1, PAGES.length - 1));
      }}
      setIsLoading={setMemberLoading}
      shouldConfirm={memberConfirmRequested}
      onConfirmHandled={() => setMemberConfirmRequested(false)}
    />,
    <PermissionsPage key="PermissionsPage" />,
  ];
  const PAGE_NUMBERS = useMemo(
    () => Array.from({ length: PAGES.length }, (_, idx) => idx),
    [PAGES.length],
  );
  return (
    <Form
      onSubmit={submitForm}
      options={{
        defaultValues: {
          scopesConsented: unflatten(
            Object.fromEntries(
              displayedScopes.map((scope: string) => [scope, true]),
            ),
          ),
          rememberConsent: true,
        },
      }}
    >
      <ContentWrapper>
        <PageIndicators pageNumbers={PAGE_NUMBERS} currentPage={currentPage} />
        <PageWrapper>{PAGES[currentPage]}</PageWrapper>
      </ContentWrapper>
      <Footer
        clickNext={clickNext}
        clickBack={clickBack}
        buttonText={BUTTON_TEXT[currentPage]}
        showBackButton={!!currentPage}
        buttonType={
          currentPage === PAGE_NUMBERS.length - 1 ? "submit" : "button"
        }
        isDisabled={isButtonsDisabled || memberLoading}
      />
    </Form>
  );
};
export default Pages;
